import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import NotificationToast from '../components/NotificationToast';
import profileService from '../services/profileService';
import { SettingsSkeleton } from '../components/SkeletonLoader';

const Settings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [nickname, setNickname] = useState('');
  const [avatar, setAvatar] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [notification, setNotification] = useState(null);
  const [availableAvatars, setAvailableAvatars] = useState([]);
  const [showAvatarSelector, setShowAvatarSelector] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check authentication
    const userStr = localStorage.getItem('user');
    let userData = null;
    try {
      userData = userStr ? JSON.parse(userStr) : null;
    } catch (e) {
      userData = null;
    }
    if (!userData || !userData.name) {
      navigate('/login', { replace: true });
      return;
    }

    // Load profile data from backend
    loadProfileData();
    loadAvailableAvatars();
    
    // Set loading to false after data is loaded
    setTimeout(() => {
      setLoading(false);
    }, 1500);
  }, [navigate, user]);

  const loadProfileData = async () => {
    try {
      const profileData = await profileService.getProfile();
      setNickname(profileData.nickname || '');
      setAvatar(profileData.avatar_url || '');
    } catch (error) {
      console.error('Error loading profile data:', error);
      setNotification({ message: 'Failed to load profile data', type: 'error' });
    }
  };

  const loadAvailableAvatars = async () => {
    try {
      console.log('🔄 Loading available avatars...');
      const avatars = await profileService.getAvailableAvatars();
      console.log('✅ Avatars loaded:', avatars);
      setAvailableAvatars(avatars);
    } catch (error) {
      console.error('❌ Error loading avatars:', error);
    }
  };

  const handleSaveProfile = async () => {
    try {
      await profileService.updateProfile({
        nickname: nickname,
        avatar_url: avatar
      });
      
      setNotification({ message: 'Profile updated successfully!', type: 'success' });
      setIsEditing(false);
    } catch (error) {
      setNotification({ message: 'Failed to update profile', type: 'error' });
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setAvatar(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAvatarSelect = (avatar) => {
    setSelectedAvatar(avatar);
    setAvatar(profileService.getAvatarUrl(avatar.filename));
  };

  const handleAvatarConfirm = async () => {
    if (selectedAvatar) {
      try {
        await profileService.updateProfile({
          avatar_url: profileService.getAvatarUrl(selectedAvatar.filename)
        });
        setAvatar(profileService.getAvatarUrl(selectedAvatar.filename));
        setShowAvatarSelector(false);
        setSelectedAvatar(null);
        setNotification({ message: 'Avatar updated successfully!', type: 'success' });
      } catch (error) {
        setNotification({ message: 'Failed to update avatar', type: 'error' });
      }
    }
  };

  const clearNotification = () => {
    setNotification(null);
  };

  // Show skeleton while loading
  if (loading) {
    return <SettingsSkeleton />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-lg border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white transform transition-all duration-200 hover:scale-105 hover:translate-x-1"
              >
                ← Back
              </button>
              <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
                Settings
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 animate-fade-in">
        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-slide-up">
          {/* Profile Settings */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-200 dark:border-gray-700 transform transition-all duration-300 hover:scale-105 hover:shadow-xl hover:border-blue-200 dark:hover:border-blue-700">
            <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
              <span className="text-2xl animate-pulse">👤</span>
              Profile Settings
            </h2>
            
            <div className="space-y-4">
              {/* Avatar Section */}
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <img
                    src={avatar || profileService.getUserAvatarUrl()}
                    alt="Profile"
                    className="w-16 h-16 rounded-full object-cover border-3 border-white dark:border-gray-700 shadow-md transform transition-all duration-300 hover:scale-110 hover:shadow-lg"
                  />
                  <button
                    onClick={() => setShowAvatarSelector(true)}
                    className="absolute -bottom-1 -right-1 w-6 h-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-xs hover:from-blue-600 hover:to-purple-700 hover:scale-110 transform transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    ✏️
                  </button>
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-gray-800 dark:text-white mb-1">
                    Profile Picture
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Choose from available avatars
                  </p>
                </div>
              </div>

              {/* Nickname Section */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Nickname
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    placeholder="Enter your nickname"
                    className="flex-1 p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transform transition-all duration-200 hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-sm"
                  />
                  <button
                    onClick={handleSaveProfile}
                    className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 hover:scale-105 transform transition-all duration-200 text-sm shadow-md hover:shadow-lg"
                  >
                    Save
                  </button>
                </div>
              </div>

              {/* Custom Avatar Upload */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Upload Custom Avatar
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  className="block w-full text-xs text-gray-500 dark:text-gray-400 file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-gray-700 dark:file:text-gray-300"
                />
              </div>
            </div>
          </div>



          {/* Account Settings */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-200 dark:border-gray-700 transform transition-all duration-300 hover:scale-105 hover:shadow-xl hover:border-green-200 dark:hover:border-green-700">
            <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
              <span className="text-2xl">⚙️</span>
              Account Settings
            </h2>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg transform transition-all duration-200 hover:scale-102 hover:bg-gray-100 dark:hover:bg-gray-600 hover:shadow-md cursor-pointer group">
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                    Email
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {user?.email || 'user@example.com'}
                  </p>
                </div>
                <button className="px-3 py-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-semibold text-sm transform transition-all duration-200 hover:scale-105">
                  Change
                </button>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg transform transition-all duration-200 hover:scale-102 hover:bg-gray-100 dark:hover:bg-gray-600 hover:shadow-md cursor-pointer group">
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                    Password
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Last changed 30 days ago
                  </p>
                </div>
                <button className="px-3 py-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-semibold text-sm transform transition-all duration-200 hover:scale-105">
                  Change
                </button>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg transform transition-all duration-200 hover:scale-102 hover:bg-gray-100 dark:hover:bg-gray-600 hover:shadow-md cursor-pointer group">
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                    Privacy
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Manage your privacy settings
                  </p>
                </div>
                <button className="px-3 py-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-semibold text-sm transform transition-all duration-200 hover:scale-105">
                  Manage
                </button>
              </div>
            </div>
          </div>

          {/* Data & Storage */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-200 dark:border-gray-700 transform transition-all duration-300 hover:scale-105 hover:shadow-xl hover:border-orange-200 dark:hover:border-orange-700">
            <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
              <span className="text-2xl animate-ping">💾</span>
              Data & Storage
            </h2>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg transform transition-all duration-200 hover:scale-102 hover:bg-gray-100 dark:hover:bg-gray-600 hover:shadow-md cursor-pointer group">
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                    Export Data
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Download your learning data
                  </p>
                </div>
                <button className="px-3 py-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-semibold text-sm transform transition-all duration-200 hover:scale-105">
                  Export
                </button>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg transform transition-all duration-200 hover:scale-102 hover:bg-gray-100 dark:hover:bg-gray-600 hover:shadow-md cursor-pointer group">
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-white group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors duration-200">
                    Clear Cache
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Free up storage space
                  </p>
                </div>
                <button className="px-3 py-1 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 font-semibold text-sm transform transition-all duration-200 hover:scale-105">
                  Clear
                </button>
              </div>
            </div>
          </div>

          {/* Support */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-200 dark:border-gray-700 transform transition-all duration-300 hover:scale-105 hover:shadow-xl hover:border-red-200 dark:hover:border-red-700">
            <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
              <span className="text-2xl animate-pulse">🆘</span>
              Support
            </h2>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg transform transition-all duration-200 hover:scale-102 hover:bg-gray-100 dark:hover:bg-gray-600 hover:shadow-md cursor-pointer group">
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                    Help Center
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Get help and find answers
                  </p>
                </div>
                <button className="px-3 py-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-semibold text-sm transform transition-all duration-200 hover:scale-105">
                  Visit
                </button>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg transform transition-all duration-200 hover:scale-102 hover:bg-gray-100 dark:hover:bg-gray-600 hover:shadow-md cursor-pointer group">
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                    Contact Support
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Get in touch with our team
                  </p>
                </div>
                <button className="px-3 py-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-semibold text-sm transform transition-all duration-200 hover:scale-105">
                  Contact
                </button>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg transform transition-all duration-200 hover:scale-102 hover:bg-gray-100 dark:hover:bg-gray-600 hover:shadow-md cursor-pointer group">
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                    About
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Version 1.0.0
                  </p>
                </div>
                <button className="px-3 py-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-semibold text-sm transform transition-all duration-200 hover:scale-105">
                  View
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Avatar Selector Modal */}
      {showAvatarSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-800 dark:text-white">
                Choose Avatar
              </h3>
              <button
                onClick={() => setShowAvatarSelector(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>
            
            <div className="grid grid-cols-4 gap-4 mb-6">
              {availableAvatars.map((avatar) => (
                <button
                  key={avatar.id}
                  onClick={() => handleAvatarSelect(avatar)}
                  className={`p-2 rounded-xl border-2 transition-all duration-200 ${
                    selectedAvatar?.id === avatar.id
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  <img
                    src={profileService.getAvatarUrl(avatar.filename)}
                    alt={avatar.display_name}
                    className="w-full h-16 object-cover rounded-lg"
                  />
                  <p className="text-xs text-center mt-2 text-gray-600 dark:text-gray-400">
                    {avatar.display_name}
                  </p>
                </button>
              ))}
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={handleAvatarConfirm}
                disabled={!selectedAvatar}
                className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirm Selection
              </button>
              <button
                onClick={() => setShowAvatarSelector(false)}
                className="flex-1 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-white py-3 rounded-xl font-semibold hover:bg-gray-400 dark:hover:bg-gray-500 transition-all duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Toast */}
      {notification && (
        <NotificationToast
          message={notification.message}
          type={notification.type}
          onClose={clearNotification}
        />
      )}
    </div>
  );
};

export default Settings; 